import type { EntryPhase, EntrySubmission } from "@seagames/contracts";
import { useUiStrings } from "@seagames/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, type RefObject, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import mascot from "../../assets/brand/dashboard-mascot-wave.webp";
import { useNocModuleOpen } from "../../lib/noc-stages/use-noc-module-open";
import { LockIcon } from "../accreditation/icons";
import { MedalIcon, type MedalKind } from "../common/medal-icon";
import { nullableStepDown, nullableStepStart } from "./model";
import { SubmissionStamp } from "./parts";
import "./entries-play.css";

/**
 * The playful pieces both entry editors share: the − n + stepper, a number
 * that bumps when it changes, the medal disc, the sticky delegation
 * scoreboard (Save / Submit) and the short celebration after a submit.
 * Motion is CSS only and stops under `prefers-reduced-motion` (entries.css).
 */

/** A figure that replays a small bump each time its value changes. */
export function Bump({ value, children, className = "" }: { value: number; children?: ReactNode; className?: string }) {
	const { formatNumber: fmt } = useUiStrings();
	return (
		<span key={value} className={`ent-bump ${className}`}>
			{children ?? fmt(value)}
		</span>
	);
}

export type Medal = MedalKind;

/** The medal with its count beside it; the count bumps when it changes. */
export function MedalDisc({ medal, value, label }: { medal: Medal; value: number; label: string }) {
	const { formatNumber: fmt } = useUiStrings();
	return (
		<span className="ent-medal" data-zero={value === 0 || undefined} title={label}>
			<MedalIcon medal={medal} />
			<span className="sr-only">{label}</span>
			<span key={value} className="ent-bump" aria-hidden>
				{fmt(value)}
			</span>
		</span>
	);
}

/**
 * − n + with typing. `size="lg"` is the EBN event stepper. The number box
 * selects on focus; a typed value is clamped by `onValue`'s caller.
 */
export function Stepper({
	value,
	max,
	locked,
	label,
	decLabel,
	incLabel,
	onValue,
	parse,
	size = "sm",
	tone,
}: {
	value: number;
	max: number;
	locked: boolean;
	/** The input's accessible name. */
	label: string;
	decLabel: string;
	incLabel: string;
	onValue: (v: number) => void;
	/** Raw text → a clamped number. */
	parse: (raw: string) => number;
	size?: "sm" | "lg";
	/** `full` = at the cap (the box turns yellow). */
	tone?: "full";
}) {
	return (
		<span className="ent-step" data-size={size} data-on={value > 0 || undefined} data-tone={tone} data-locked={locked || undefined}>
			<button type="button" aria-label={decLabel} disabled={locked || value <= 0} onClick={() => onValue(value - 1)}>
				−
			</button>
			<input
				type="number"
				inputMode="numeric"
				min={0}
				max={max}
				value={String(value)}
				disabled={locked}
				aria-label={label}
				onFocus={(e) => e.currentTarget.select()}
				onChange={(e) => onValue(parse(e.target.value))}
			/>
			<button type="button" aria-label={incLabel} disabled={locked || value >= max} onClick={() => onValue(value + 1)}>
				+
			</button>
		</span>
	);
}

/**
 * − n + for an OPTIONAL number: blank = "not given" (null), distinct from 0.
 * − from 1 (or `min` when higher) goes back to blank, never 0
 * (`nullableStepDown`; − on blank does nothing); + from blank
 * starts at `nullableStepStart` — 1 (or `min` when higher), never above
 * `max` — so "—" → 1, not 0 (a 0 is typed); clearing the box clears it. A typed value above `max` is clamped and `onClamp` says so,
 * so the caller can explain why.
 */
export function NullableStepper({
	value,
	min = 0,
	max,
	locked,
	label,
	decLabel,
	incLabel,
	onValue,
	onClamp,
	placeholder = "—",
	maxTitle,
}: {
	value: number | null;
	min?: number;
	max: number;
	locked: boolean;
	label: string;
	decLabel: string;
	incLabel: string;
	onValue: (v: number | null) => void;
	onClamp?: () => void;
	placeholder?: string;
	/** The + button's tooltip once at `max` (why it stops). */
	maxTitle?: string;
}) {
	const atMax = value !== null && value >= max;
	return (
		<span className="ent-step" data-on={(value ?? 0) > 0 || undefined} data-blank={value === null || undefined} data-locked={locked || undefined}>
			<button type="button" aria-label={decLabel} disabled={locked || value === null} onClick={() => onValue(value === null ? null : nullableStepDown(value, min))}>
				−
			</button>
			<input
				type="number"
				inputMode="numeric"
				min={min}
				max={max}
				value={value === null ? "" : String(value)}
				placeholder={placeholder}
				disabled={locked}
				aria-label={label}
				onFocus={(e) => e.currentTarget.select()}
				onChange={(e) => {
					const raw = e.target.value.trim();
					if (raw === "") return onValue(null);
					const n = Number.parseInt(raw, 10);
					if (!Number.isFinite(n)) return onValue(null);
					onValue(Math.max(min, Math.min(n, max)));
					// After onValue, so a caller that resets its note on every value keeps this one.
					if (n > max) onClamp?.();
				}}
			/>
			<button
				type="button"
				aria-label={incLabel}
				title={atMax ? maxTitle : undefined}
				disabled={locked || atMax || max < min}
				onClick={() => onValue(value === null ? nullableStepStart(min, max) : value + 1)}
			>
				+
			</button>
		</span>
	);
}

export interface BoardStat {
	key: string;
	label: string;
	value: number;
	/** A second line under the figure ("12 men, 9 women"). */
	note?: string;
}

/**
 * The sticky scoreboard under an editor: the delegation's live figures (with
 * the draft applied) and, on the right, what can be done — unsaved →
 * Discard / Save; else Submit (or, when submitted, Reopen for a SUPER_ADMIN).
 * What may be done is the API's `can_edit` / `can_submit`.
 */
/**
 * While the sticky scoreboard is on screen, toasts (the EOI's Undo, a save's
 * outcome) rise above it instead of landing on it: the board publishes its
 * height + its sticky offset as `--gms-toast-bottom`, which the app's
 * `<Toaster>` reads (main.tsx). The board wraps to two rows on a narrow
 * screen, so it is measured, not assumed.
 */
function useToastClearance(ref: RefObject<HTMLDivElement | null>) {
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const root = document.documentElement;
		const update = () => {
			const offset = Number.parseFloat(getComputedStyle(el).bottom) || 0;
			root.style.setProperty("--gms-toast-bottom", `calc(${el.offsetHeight + offset}px + 1rem)`);
		};
		update();
		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => {
			observer.disconnect();
			root.style.removeProperty("--gms-toast-bottom");
		};
	}, [ref]);
}

export function Scoreboard({
	stats,
	medals,
	pendingCount,
	saving,
	isFinal,
	submission,
	canSubmit,
	submitDisabledReason,
	canReopen,
	onDiscard,
	onSave,
	onReview,
	onSaveAndReview,
	onReopen,
}: {
	stats: BoardStat[];
	medals?: { gold: number; silver: number; bronze: number };
	pendingCount: number;
	saving: boolean;
	isFinal: boolean;
	submission: EntrySubmission;
	/** The API's `can_submit` (the saved state). */
	canSubmit: boolean;
	submitDisabledReason?: string;
	canReopen: boolean;
	onDiscard: () => void;
	onSave: () => void;
	/** Nothing unsaved: open the review (the summary before Submit). */
	onReview: () => void;
	/** Unsaved, and the phase can still be submitted: save first, then open the review. */
	onSaveAndReview?: () => void;
	onReopen: () => void;
}) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const ref = useRef<HTMLDivElement>(null);
	useToastClearance(ref);
	return (
		<div ref={ref} role="region" aria-label={t("bar.label")} className="ent-board">
			<dl className="ent-board__stats">
				{stats.map((s) => (
					<div key={s.key} className="ent-board__stat">
						<dt>{s.label}</dt>
						<dd>
							<Bump value={s.value} className="ent-board__fig" />
							{s.note && <span className="ent-board__note">{s.note}</span>}
						</dd>
					</div>
				))}
				{medals && (
					<div className="ent-board__stat">
						<dt>{t("eoi.tiles.medals")}</dt>
						<dd className="flex items-center gap-[0.25rem]">
							<MedalDisc medal="gold" value={medals.gold} label={t("board.medalCount", { medal: t("eoi.gold"), n: fmt(medals.gold) })} />
							<MedalDisc medal="silver" value={medals.silver} label={t("board.medalCount", { medal: t("eoi.silver"), n: fmt(medals.silver) })} />
							<MedalDisc medal="bronze" value={medals.bronze} label={t("board.medalCount", { medal: t("eoi.bronze"), n: fmt(medals.bronze) })} />
						</dd>
					</div>
				)}
			</dl>
			<div className="ent-board__act" aria-live="polite">
				{pendingCount > 0 ? (
					<>
						<span className="ent-board__unsaved">
							<span aria-hidden className="ent-board__pulse" />
							{t("bar.unsaved", { count: pendingCount, n: fmt(pendingCount) })}
						</span>
						<button type="button" className="btn btn-sm ent-board__ghost" onClick={onDiscard} disabled={saving}>
							{t("bar.discard")}
						</button>
						<button type="button" className="btn btn-sm ent-board__save" onClick={onSave} disabled={saving}>
							{saving && <span className="loading loading-spinner loading-xs" />}
							{t("bar.save")}
						</button>
						{onSaveAndReview && (
							<button type="button" className="btn btn-sm btn-accent" onClick={onSaveAndReview} disabled={saving}>
								<LockIcon className="size-[0.875rem]" />
								{t("bar.saveReview")}
							</button>
						)}
					</>
				) : isFinal ? (
					<>
						<span className="ent-board__final">
							<LockIcon className="size-[0.875rem]" />
							<span>
								{t("bar.finalTitle")}
								<span className="block font-normal opacity-80">
									<SubmissionStamp submission={submission} />
								</span>
							</span>
						</span>
						{canReopen && (
							<button type="button" className="btn btn-sm ent-board__ghost" onClick={onReopen}>
								{t("bar.reopen")}
							</button>
						)}
					</>
				) : (
					canSubmit && (
						<button type="button" className="btn btn-sm btn-accent" onClick={onReview} disabled={!!submitDisabledReason} title={submitDisabledReason}>
							<LockIcon className="size-[0.875rem]" />
							{t("bar.submit")}
						</button>
					)
				)}
			</div>
		</div>
	);
}

/** The poster's corner stamp once a phase is submitted: lock, "Submitted as final", when and by whom. */
export function PosterSeal({ submission }: { submission: EntrySubmission }) {
	const { t } = useTranslation("entries");
	if (submission.state !== "FINAL") return null;
	return (
		<span className="ent-poster__seal">
			<span className="flex items-center gap-[0.3125rem] font-semibold">
				<LockIcon className="size-[0.875rem]" />
				{t("bar.finalTitle")}
			</span>
			<small>
				<SubmissionStamp submission={submission} />
			</small>
		</span>
	);
}

/**
 * Under a submitted poster: a NOC is told who to ask; someone the API still
 * lets edit (an admin) gets "Change anyway", which swaps in the editor.
 */
export function PosterFoot({ submission, onEdit, onReopen }: { submission: EntrySubmission; onEdit?: () => void; onReopen?: () => void }) {
	const { t } = useTranslation("entries");
	if (submission.state !== "FINAL") return null;
	if (!onEdit && !onReopen) return <p className="ent-poster__foot">{t("final.locked")}</p>;
	return (
		<div className="ent-poster__foot flex flex-wrap items-center justify-between gap-[0.5rem]">
			<span>{onEdit ? t("final.adminEdit") : t("final.locked")}</span>
			<span className="flex flex-wrap gap-[0.5rem]">
				{onReopen && (
					<button type="button" className="btn btn-sm btn-line" onClick={onReopen}>
						{t("bar.reopen")}
					</button>
				)}
				{onEdit && (
					<button type="button" className="btn btn-sm btn-line" onClick={onEdit}>
						{t("poster.edit")}
					</button>
				)}
			</span>
		</div>
	);
}

/**
 * The moment after a successful submit: a short burst of navy / red / yellow
 * confetti around the waving mascot and a card naming the next step. The
 * confetti is skipped under reduced motion; the card stays until closed.
 */
export function Celebration({ phase, search, onClose }: { phase: EntryPhase; search: { games_id?: string; org?: string }; onClose: () => void }) {
	const { t } = useTranslation("entries");
	const closeRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		closeRef.current?.focus();
		const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);
	const key = phase === "EOI" ? "EOI" : "EBN";
	// The next step must be open for this NOC (Sector 2 for registration): never offer a button into a locked page.
	const nextOpen = useNocModuleOpen(phase === "EOI" ? "entries" : "registration");
	return (
		<div className="ent-party" role="dialog" aria-modal="false" aria-labelledby="ent-party-h">
			<div className="ent-party__confetti" aria-hidden>
				{Array.from({ length: 18 }, (_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: fixed decoration
					<i key={i} style={{ "--i": i } as React.CSSProperties} />
				))}
			</div>
			<div className="ent-party__card">
				<img src={mascot} alt="" width={372} height={560} className="h-[5.5rem] w-auto" />
				<div className="grid gap-[0.25rem]">
					<h2 id="ent-party-h" className="text-[1rem] font-bold text-gms-navy-ink">
						{t(`celebrate.${key}.title`)}
					</h2>
					<p className="text-[0.8125rem] text-gms-ink-2">{nextOpen ? t(`celebrate.${key}.body`) : t("celebrate.EBN.bodyLocked")}</p>
				</div>
				<div className="flex flex-wrap justify-center gap-[0.5rem]">
					<button ref={closeRef} type="button" className="btn btn-sm btn-line" onClick={onClose}>
						{t("celebrate.close")}
					</button>
					{nextOpen &&
						(phase === "EOI" ? (
							<Link to="/accreditation/entries/ebn" search={{ games_id: search.games_id, org: search.org }} className="btn btn-sm btn-primary" onClick={onClose}>
								{t("celebrate.EOI.go")}
							</Link>
						) : (
							<Link to="/accreditation/add" search={{ games_id: search.games_id }} className="btn btn-sm btn-primary" onClick={onClose}>
								{t("celebrate.EBN.go")}
							</Link>
						))}
				</div>
			</div>
		</div>
	);
}
